// src/modules/app-views/_apps/appsindico/units/pages/Document/tabs/RecordTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Row, Col } from 'antd';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleTextArea from 'components/SimpleComponents/SimpleTextArea';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { useMemo, useCallback, useState } from 'react';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const RecordTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { isWorking } = useDocumentContext();
  const storedModule = useZustand('module', 'config');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const moduleId = storedModule?.id || 'units';

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE (ANTD v6 fix: controlled open for each Select)
  // ─────────────────────────────────────────────────────────────────────────────
  const [blockOpen, setBlockOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL VARIABLES II — build options from stored module parameters
  // ─────────────────────────────────────────────────────────────────────────────
  const blockOptions = useMemo(() => {
    const raw = appService.getModulePreferences({ moduleId, category: 'blocks' });
    if (!Array.isArray(raw)) return [];
    return raw
      .filter(b => !b.removed)
      .map(b => ({
        value: b.uid,
        label: b.name,
        abbreviation: b.abbreviation,
        backColor: b.backColor,
        foreColor: b.foreColor,
      }));
  }, [moduleId]);

  const profileOptions = useMemo(() => {
    const raw = appService.getModulePreferences({ moduleId, category: 'profiles' });
    if (!Array.isArray(raw)) return [];
    return raw
      .filter(p => !p.removed)
      .map(p => ({
        value: p.uid,
        label: p.name,
        m2: p.m2,
        unitDigits: p.unitDigits,
      }));
  }, [moduleId]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS (ANTD v6 fix: controlled open state ensures dropdown closes on first click)
  // ─────────────────────────────────────────────────────────────────────────────
  const handleBlockSelect = useCallback(() => setBlockOpen(false), []);
  const handleBlockOpenChange = useCallback(v => setBlockOpen(v), []);
  const handleProfileSelect = useCallback(() => setProfileOpen(false), []);
  const handleProfileOpenChange = useCallback(v => setProfileOpen(v), []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Row 1: unit number, block, profile */}
      <Row gutter={ROW_GUTTER}>
        <Col xs={24} sm={6}>
          <SimpleInput name="room" label="unit_number" required />
        </Col>
        <Col xs={24} sm={9}>
          <SimpleSelect
            name="blockId"
            label="block"
            options={blockOptions}
            open={blockOpen}
            onSelect={handleBlockSelect}
            onOpenChange={handleBlockOpenChange}
            allowClear
          />
        </Col>
        <Col xs={24} sm={9}>
          <SimpleSelect
            name="profileId"
            label="profile"
            options={profileOptions}
            open={profileOpen}
            onSelect={handleProfileSelect}
            onOpenChange={handleProfileOpenChange}
            allowClear
          />
        </Col>
      </Row>

      {/* Row 2: status, habitation type */}
      <Row gutter={ROW_GUTTER}>
        <Col xs={24} sm={8}>
          <SimpleSelect
            name="status"
            label="status"
            options={[
              { value: 'vacant', label: translateX('vacant') },
              { value: 'occupied', label: translateX('occupied') },
              { value: 'maintenance', label: translateX('maintenance') },
              { value: 'reserved', label: translateX('reserved') },
            ]}
          />
        </Col>
        <Col xs={24} sm={8}>
          <SimpleSelect
            name="habitationType"
            label="habitation_type"
            options={[
              { value: 'residential', label: translateX('residential') },
              { value: 'commercial', label: translateX('commercial') },
              { value: 'mixed', label: translateX('mixed') },
            ]}
          />
        </Col>
        <Col xs={24} sm={8}>
          <SimpleNumber name="floor" label="floor" />
        </Col>
      </Row>

      {/* Row 3: area, observation */}
      <Row gutter={ROW_GUTTER}>
        <Col xs={24} sm={8}>
          <SimpleNumber name="area" label="area_m2" />
        </Col>
        <Col xs={24} sm={16}>
          <SimpleTextArea name="observation" rows={3} />
        </Col>
      </Row>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default RecordTab;
